import { useEffect, useRef, useState } from 'react';
import { Plus, ShieldCheck } from 'lucide-react';
import { apiRequest } from '../../api/http';
import { WorkspaceLayout } from '../../layouts/WorkspaceLayout';
import { EmptyState, ErrorState, Pagination, SearchBar, Spinner, StatusBadge } from '../../shared/components';
import './adminAccounts.css';
import { useAuth } from '../../auth/AuthContext';
import { ADMIN_ROLES, adminGroups, canAdmin, canAssignAdminRole, type AdminGroup } from '../../auth/adminAccess';
import { Modal } from '../../shared/components';

type Account = {
  id: string;
  display_name: string;
  email?: string;
  tel?: string;
  role: string;
  account_roles?: string[];
  admin_groups?: string[];
  status: string;
  admin_stack?: { enabled?: boolean; role?: string; status?: string };
};
type AccountList = { items: Account[]; pagination: { total: number; total_pages: number } };
const isAdmin = (account: Account) => adminGroups(account).length > 0;
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง';
const rolePermissions: { label: string; groups: AdminGroup[] }[] = [
  { label: 'แก้ไข/ลบ Content และรีวิว', groups: ['editor'] },
  { label: 'ตรวจคุณภาพรีวิว ผ่าน/ไม่ผ่าน', groups: ['editor'] },
  { label: 'ตรวจ Creator และหลักฐานยืนยันตัวตน', groups: ['editor'] },
  { label: 'Creator Monitor / ผลงาน / Network', groups: ['editor'] },
  { label: 'ตรวจ Merchant / อนุมัติ claim ร้าน', groups: ['campaign'] },
  { label: 'จัดการข้อมูลร้านค้า', groups: ['campaign'] },
  { label: 'สร้าง/แก้/เปิด/พัก/Archive Campaign', groups: ['campaign'] },
  { label: 'อนุมัติเผยแพร่ Merchant Campaign', groups: ['campaign'] },
  { label: 'จัดการแพ็กเกจ จำนวนรีวิว ระยะเวลา', groups: ['campaign'] },
  { label: 'กำหนดราคาแพ็กเกจ / Reward / งบ', groups: ['finance'] },
  { label: 'อนุมัติแต้มรีวิวเข้า Wallet', groups: ['finance'] },
  { label: 'อนุมัติเติมเงิน / ถอนเงิน / ยืนยันโอน', groups: ['finance'] },
  { label: 'เติมงบ Campaign / จัดการเงินคงเหลือ', groups: ['finance'] },
  { label: 'Ledger / Reversal / Reconciliation / Risk', groups: ['finance'] },
  { label: 'กำหนดอัตราแลก Point / บาท', groups: ['finance'] },
  { label: 'รายงานการเงิน / Export', groups: ['finance'] },
  { label: 'KPI ภาพรวมระบบ', groups: ['campaign', 'system'] },
  { label: 'ตั้งค่า Home blocks และระบบ', groups: ['system'] },
  { label: 'ทดสอบ Email/SMS / ดู Delivery Log', groups: ['system'] },
  { label: 'Presentation สำหรับนำเสนอร้านค้า', groups: ['campaign', 'system'] },
  { label: 'เพิ่ม/ถอน Admin และกำหนด Role', groups: ['system'] },
];

function RoleDetails() {
  return <div className="admin-role-details">
    <p>ตารางนี้แสดงกลุ่มงานหลักของแต่ละ Role โดย System เป็นสิทธิ์สูงสุดและเข้าถึงทุกกลุ่มได้</p>
    <div className="table-scroll"><table className="admin-role-details__table">
      <thead><tr><th scope="col">กลุ่มงาน / สิทธิ์</th>{ADMIN_ROLES.map(role => <th scope="col" key={role.id}>{role.title}</th>)}</tr></thead>
      <tbody>{rolePermissions.map(permission => <tr key={permission.label}>
        <th scope="row">{permission.label}</th>
        {ADMIN_ROLES.map(role => <td key={role.id} aria-label={permission.groups.includes(role.id) ? `${role.title}: มีสิทธิ์` : `${role.title}: —`}>{permission.groups.includes(role.id) ? '✓' : '—'}</td>)}
      </tr>)}</tbody>
    </table></div>
    <h3>ชื่อ Role ในระบบ</h3>
    <div className="table-scroll"><table className="admin-role-details__names">
      <thead><tr><th scope="col">Role</th><th scope="col">ชื่อแสดง</th><th scope="col">ขอบเขต</th></tr></thead>
      <tbody><tr><td><code>admin_editor</code></td><td>Editor</td><td>Content, Review, Creator</td></tr>
        <tr><td><code>admin_campaign</code></td><td>Marketing</td><td>Merchant, ร้านค้า, Campaign</td></tr>
        <tr><td><code>admin_finance</code></td><td>Finance</td><td>เงิน แต้ม งบ และการจ่าย</td></tr>
        <tr><td><code>admin_system</code></td><td>System</td><td>ตั้งค่าระบบและจัดการสิทธิ์</td></tr></tbody>
    </table></div>
  </div>;
}

function useAccounts(query: string, page: number, revision: number, admins: boolean, enabled: boolean) {
  const [data, setData] = useState<AccountList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError('');
    if (!enabled || (!admins && !query.trim())) { setLoading(false); return; }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await apiRequest<AccountList>('/admin/accounts', {
          query: { role: admins ? 'admin' : undefined, q: query.trim(), sort_by: 'account', sort_dir: 'asc', page, page_size: 20 },
          signal: controller.signal,
        });
        if (!controller.signal.aborted) setData(result);
      } catch (reason) {
        if (!controller.signal.aborted) setError(errorMessage(reason));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, admins ? 0 : 300);
    return () => { controller.abort(); window.clearTimeout(timer); };
  }, [query, page, revision, admins, enabled]);
  return { data, loading, error };
}

export function AdminAccountsPage() {
  const auth = useAuth();
  const canManage = canAdmin(auth.session, 'system');
  const [selected, setSelected] = useState<Account | null>(null);
  const [adding, setAdding] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<AdminGroup[]>([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [draftQuery, setDraftQuery] = useState('');
  const [searchRevision, setSearchRevision] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState('');
  const savingRef = useRef(false);
  const [message, setMessage] = useState('');
  const [mutationError, setMutationError] = useState('');
  const admins = useAccounts('', page, revision, true, canManage);
  const search = useAccounts(query, searchPage, revision + searchRevision, false, canManage);
  // Place/merchant records in System search are not login accounts.
  const candidates = search.data?.items.filter(account => account.id.startsWith('expert_')) || [];

  function editRoles(account: Account) {
    // Picking someone from the add-admin search hands over to the role editor.
    setAdding(false);
    setSelected(account);
    setSelectedGroups(adminGroups(account));
    setMutationError('');
  }

  async function saveRoles() {
    if (!selected || savingRef.current || !canManage) return;
    savingRef.current = true;
    setSaving(selected.id);
    setMessage('');
    setMutationError('');
    try {
      await apiRequest<Account>(`/admin/accounts/${encodeURIComponent(selected.id)}/admin-groups`, {
        method: 'PUT', body: { admin_groups: selectedGroups },
      });
      setMessage(`บันทึกสิทธิ์ของ ${selected.display_name} แล้ว`);
      setSelected(null);
      setPage(1);
      setRevision(value => value + 1);
      if (selected.id === auth.session?.expert_id) await auth.refresh();
    } catch (reason) {
      setMutationError(errorMessage(reason));
    } finally {
      savingRef.current = false;
      setSaving('');
    }
  }

  return <WorkspaceLayout kind="admin" title="Admin" description="จัดการผู้ดูแลระบบ เลือกได้หลาย Role โดย System เป็นสิทธิ์สูงสุด">
    <section className="admin-role-info" aria-label="สิทธิ์ Admin ทั้ง 4 กลุ่ม">
      {ADMIN_ROLES.map(role => <article key={role.id} className={role.id === 'system' ? 'admin-role-info__system' : ''}>
        <h2>{role.title}{role.id === 'system' && <span className="badge badge--success">สูงสุด</span>}</h2><p>{role.description}</p>
      </article>)}
    </section>
    <details className="panel admin-role-disclosure"><summary>ดูรายละเอียดสิทธิ์แต่ละ Role</summary><RoleDetails /></details>
    {!canManage && <section className="panel admin-accounts"><p>สิทธิ์ของคุณ: {adminGroups(auth.session).map(group => ADMIN_ROLES.find(role => role.id === group)?.title).join(' + ')}</p><p>เฉพาะ System เท่านั้นที่เพิ่มผู้ดูแลและแก้ไข Role ได้</p></section>}
    {canManage && <>
    <section className="panel admin-accounts" aria-labelledby="admin-list-title">
      <div className="admin-accounts__heading"><ShieldCheck size={20} /><div><h2 id="admin-list-title">รายชื่อ Admin{admins.data ? ` (${admins.data.pagination.total})` : ''}</h2><p>เรียงตามชื่อ A–Z / ก–ฮ</p></div><button className="button button--primary admin-accounts__add" onClick={() => { setMutationError(''); setMessage(''); setAdding(true); }}><Plus size={17} />เพิ่ม Admin</button></div>
      {message && <p className="form-success" role="status">{message}</p>}
      {admins.loading ? <Spinner /> : admins.error ? <ErrorState message={admins.error} retry={() => setRevision(value => value + 1)} /> : admins.data?.items.length ? <>
        <div className="table-scroll"><table><thead><tr><th>ชื่อ</th><th>อีเมล</th><th>เบอร์โทรศัพท์</th><th>สิทธิ์</th><th>สถานะ</th><th>จัดการ</th></tr></thead><tbody>{admins.data.items.map(account => <tr key={account.id}><td><strong>{account.display_name}</strong></td><td>{account.email || '—'}</td><td>{account.tel || '—'}</td><td>{adminGroups(account).map(group => <span className="badge badge--neutral" key={group}>{ADMIN_ROLES.find(role => role.id === group)?.title}</span>)}</td><td><StatusBadge value={account.status} /></td><td><button className="button button--ghost" onClick={() => editRoles(account)}>จัดการ Role</button></td></tr>)}</tbody></table></div>
        <Pagination page={page} totalPages={admins.data.pagination.total_pages} onChange={setPage} />
      </> : <EmptyState title="ยังไม่มี Admin" description="กด “เพิ่ม Admin” เพื่อค้นหาผู้ใช้" />}
    </section>
    </>}
    {adding && canManage && <Modal title="เพิ่ม Admin" onClose={() => setAdding(false)}>
      <div className="admin-accounts admin-accounts--modal">
        <p className="admin-accounts__hint">ค้นหาผู้ใช้ แล้วเลือก Role ที่ต้องการ ให้ใช้งานได้มากกว่าหนึ่งกลุ่ม</p>
      <form className="admin-accounts__search-form" onSubmit={event => { event.preventDefault(); setQuery(draftQuery.trim()); setSearchPage(1); setSearchRevision(value => value + 1); }}>
        <SearchBar value={draftQuery} onChange={setDraftQuery} placeholder="ค้นหาชื่อ อีเมล หรือเบอร์โทรศัพท์ แล้วกด Enter" />
        <button className="button button--primary" type="submit">ค้นหา</button>
      </form>
      {mutationError && <p className="form-error" role="alert">{mutationError}</p>}
      {!query.trim() ? <p className="admin-accounts__hint">พิมพ์ชื่อเพื่อค้นหาบัญชีที่ต้องการเพิ่ม</p> : search.loading ? <Spinner label="กำลังค้นหาผู้ใช้" /> : search.error ? <ErrorState message={search.error} retry={() => setRevision(value => value + 1)} /> : <>
        {candidates.length ? <ul className="admin-accounts__results">{candidates.map(account => <li key={account.id}>
          <div className="admin-accounts__identity"><strong>{account.display_name}</strong><small>{[account.email, account.tel].filter(Boolean).join(' · ') || account.id}</small></div>
          <button className="button button--primary" disabled={Boolean(saving) || !canAssignAdminRole(account)} onClick={() => editRoles(account)}>{!canAssignAdminRole(account) ? 'บัญชีไม่พร้อมใช้งาน' : isAdmin(account) ? 'จัดการ Role' : 'เลือก Role'}</button>
        </li>)}</ul> : <EmptyState title="ไม่พบบัญชีผู้ใช้ในหน้านี้" description="ลองค้นหาด้วยชื่ออื่น หรือดูผลลัพธ์หน้าถัดไป" />}
        {search.data && search.data.pagination.total_pages > 1 && <Pagination page={searchPage} totalPages={search.data.pagination.total_pages} onChange={setSearchPage} />}
      </>}
      </div>
    </Modal>}
    {selected && <Modal title={`จัดการ Role · ${selected.display_name}`} onClose={() => { if (!saving) setSelected(null); }}>
      <div className="admin-role-editor">
        <p>เลือกได้หลายกลุ่ม หากเลือก System จะได้รับสิทธิ์ทุกกลุ่ม</p>
        <details className="admin-role-disclosure admin-role-disclosure--editor"><summary>ดูรายละเอียดสิทธิ์แต่ละ Role</summary><RoleDetails /></details>
        {ADMIN_ROLES.map(role => <label className="admin-role-option" key={role.id}>
          <input type="checkbox" checked={selectedGroups.includes(role.id)} disabled={Boolean(saving) || (selected.id === auth.session?.expert_id && role.id === 'system')} onChange={event => setSelectedGroups(groups => event.target.checked ? [...groups, role.id] : groups.filter(group => group !== role.id))} />
          <span><strong>{role.title}{role.id === 'system' ? ' · สิทธิ์สูงสุด' : ''}</strong><small>{role.description}</small></span>
        </label>)}
        {!selectedGroups.length && <p className="admin-accounts__hint">ไม่เลือกกลุ่มใด = ถอนสิทธิ์ Admin โดยคงบัญชีและบทบาทอื่นไว้</p>}
        {selected.id === auth.session?.expert_id && <p className="admin-accounts__hint">ไม่สามารถถอนสิทธิ์ System ของตนเองได้</p>}
        {mutationError && <p className="form-error" role="alert">{mutationError}</p>}
        <div className="form-actions"><button className="button button--ghost" disabled={Boolean(saving)} onClick={() => setSelected(null)}>ยกเลิก</button><button className="button button--primary" disabled={Boolean(saving)} onClick={() => void saveRoles()}>{saving ? 'กำลังบันทึก...' : 'บันทึก Role'}</button></div>
      </div>
    </Modal>}
  </WorkspaceLayout>;
}
